<script setup lang="ts">
import {computed, onMounted, ref, watchEffect} from "vue"
import {SidebarLayout} from "@hantera/design-system"
import {useContext} from "@hantera/portal-app"
import useBackend from "@/composition/useBackend"
import type {AppInfo} from "@/backend/apps.types"
import AppSidebar from "./AppSidebar.vue"
import AppDetails from "./AppDetails.vue"
import MarketplaceHome from "./MarketplaceHome.vue"

const context = useContext()
const { apps: appsService } = useBackend()

watchEffect(() => {
  if (context.currentView.value?.name) {
    context.currentView.value.name.value = "Apps"
  }
})

const apps = ref<AppInfo[]>([])
const selectedAppId = ref<string | null>(null)
const isLoading = ref(true)
const loadError = ref<string | null>(null)

const selectedApp = computed(() => {
  if (!selectedAppId.value) return null
  return apps.value.find((app) => app.id === selectedAppId.value) || null
})

async function loadApps() {
  isLoading.value = true
  loadError.value = null

  try {
    apps.value = await appsService.getApps()
  } catch (error: unknown) {
    if (error && typeof error === "object" && "message" in error) {
      loadError.value = (error as { message: string }).message
    } else {
      loadError.value = "Failed to load apps"
    }
  } finally {
    isLoading.value = false
  }
}

function handleSelect(appId: string | null) {
  selectedAppId.value = appId
}

function handleInstalled(app: AppInfo) {
  const existingIndex = apps.value.findIndex((a) => a.id === app.id)
  if (existingIndex >= 0) {
    apps.value[existingIndex] = app
  } else {
    apps.value.push(app)
  }
  selectedAppId.value = app.id
}

onMounted(() => {
  loadApps()
})
</script>

<template>
  <SidebarLayout cache-key="apps-view">
    <template #sidebar>
      <AppSidebar
        :apps="apps"
        :selected-app-id="selectedAppId"
        :loading="isLoading"
        @select="handleSelect"
      />
    </template>
    <template #content>
      <div v-if="loadError" class="error-state">
        <div class="error-message">{{ loadError }}</div>
        <button class="btn-primary" @click="loadApps">Retry</button>
      </div>
      <template v-else>
        <AppDetails v-if="selectedApp" :app="selectedApp" />
        <MarketplaceHome v-else @installed="handleInstalled" />
      </template>
    </template>
  </SidebarLayout>
</template>

<style scoped>
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 1rem;
  padding: 2rem;
}

.error-message {
  font-size: 0.875rem;
  color: hsl(0 70% 45%);
  text-align: center;
}

.btn-primary {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  background-color: hsl(var(--color-primary));
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
}
</style>
